import { Link } from '../Link.tsx';
import { ThemeToggle } from '../ThemeToggle.tsx';
import { docsPages } from '../../pages/docs/docsNav.ts';
import { useT } from '../../i18n.ts';

export function MarketingFooter() {
  const t = useT();
  // Render the year as a string so MF2's number formatter doesn't apply
  // thousands grouping (which turned 2026 into "2,026"). A copyright year
  // is a label, not a quantity.
  const year = String(new Date().getFullYear());

  // TanStack Router does nothing when a link's href matches the current location —
  // scroll to top so the user isn't stranded at the bottom on mobile. Delegated
  // from the footer so it covers every link without a per-link handler.
  const handleClick = (e: MouseEvent) => {
    if (!(e.target instanceof Element)) return;
    const anchor = e.target.closest('a');
    if (!anchor) return;
    const target = new URL(anchor.href);
    if (
      target.pathname === window.location.pathname &&
      target.search === window.location.search
    ) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <footer className="mkt-footer" onClick={handleClick} data-gp-name="footer">
      <div className="mkt-footer-inner">
        <div className="mkt-footer-brand">
          <Link to="/" className="mkt-brand">
            Goalpost
          </Link>
          <p className="mkt-footer-tagline">
            {t('marketing-footer.tagline')}
          </p>
        </div>
        <div className="mkt-footer-cols">
          <div className="mkt-footer-col">
            <h5>{t('marketing-footer.col.features')}</h5>
            <ul>
              <li>
                <Link to="/features/documents">{t('marketing.feature.documents.title')}</Link>
              </li>
              <li>
                <Link to="/features/ai">{t('marketing.feature.ai.title')}</Link>
              </li>
              <li>
                <Link to="/features/approvals">{t('marketing.feature.approvals.title')}</Link>
              </li>
            </ul>
          </div>
          <div className="mkt-footer-col">
            <h5>{t('marketing-footer.col.learn-more')}</h5>
            <ul>
              {docsPages.map((page) => (
                <li key={page.path}>
                  <Link to={page.path}>{t(page.labelKey)}</Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="mkt-footer-col">
            <h5>{t('marketing-footer.col.about-goalpost')}</h5>
            <ul>
              <li>
                <Link to="/blog">{t('marketing-footer.link.blog')}</Link>
              </li>
              <li>
                <Link to="/pricing">{t('marketing-footer.link.pricing')}</Link>
              </li>
              <li>
                <Link to="/register">{t('marketing-footer.link.signup')}</Link>
              </li>
              <li>
                <Link to="/dashboard">{t('marketing.cta.login')}</Link>
              </li>
              <li>
                <Link to="/privacy">{t('marketing-footer.link.privacy')}</Link>
              </li>
              <li>
                <Link to="/terms">{t('marketing-footer.link.terms')}</Link>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="mkt-footer-legal">
        <span>{t('marketing-footer.copyright', { year })}</span>
        <ThemeToggle showLabel />
      </div>
    </footer>
  );
}
